import { NavIcon } from '../chrome/icons/NavIcon';
import { ICON_SETS, type IconSetId } from './icons';
import {
  CUSTOM_THEMES,
  THEME_GROUPS,
  type ResolvedTheme,
  type ThemeGroup,
  type ThemePreference,
} from './theme';
import { useTheme } from './useTheme';

interface ThemeOption {
  id: ThemePreference;
  label: string;
  description: string;
  group: ThemeGroup;
  /** `data-theme` to render the swatch chips in — 'system' has no CSS block of its own, so
   * its preview borrows whatever the OS currently resolves to. */
  swatchTheme: ResolvedTheme;
}

/** Four color chips (bg/surface, accent, and two member colors) rendered *inside* a
 * `data-theme` wrapper — real CSS custom properties, not a hardcoded JS color table, so a
 * new theme in tokens.css shows up here automatically. */
function ThemeSwatch({ theme }: { theme: ResolvedTheme }): JSX.Element {
  return (
    <div
      data-theme={theme}
      className="flex items-center gap-1 rounded-md border border-line bg-bg p-1"
      aria-hidden="true"
    >
      <span className="h-5 w-5 rounded-sm border border-line bg-surface" />
      <span className="h-5 w-5 rounded-sm bg-accent" />
      <span className="h-5 w-5 rounded-full bg-member-faye" />
      <span className="h-5 w-5 rounded-full bg-member-ezry" />
    </div>
  );
}

/** One 44px radio row. Shared by both pickers so a theme row and an icon row are visually
 * the same object — same hit area, same radio dot, preview on the right. */
function PickerRow({
  selected,
  label,
  description,
  testId,
  onSelect,
  preview,
}: {
  selected: boolean;
  label: string;
  description: string;
  testId: string;
  onSelect: () => void;
  preview: JSX.Element;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-testid={testId}
      onClick={onSelect}
      className="flex min-h-[44px] w-full items-center justify-between gap-3 py-2 text-left ease-app"
    >
      <span className="flex min-w-0 items-center gap-3">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
            selected ? 'border-accent' : 'border-line'
          }`}
        >
          {selected && <span className="h-2.5 w-2.5 rounded-full bg-accent" />}
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium text-fg">{label}</span>
          <span className="block text-xs leading-snug text-fg-3">{description}</span>
        </span>
      </span>
      {preview}
    </button>
  );
}

/** Sub-heading above each group of rows. Small, wide-tracked, `fg-3` — a label, not a
 * competing headline: "Appearance" is still the only real `h2` in the section. */
function GroupHeading({ children }: { children: string }): JSX.Element {
  return (
    <h4 className="pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.13em] text-fg-3 first:pt-0">
      {children}
    </h4>
  );
}

/**
 * "Appearance → Theme" picker (docs/DECISIONS.md 2026-07-31, regrouped 2026-08-01). Ten
 * options is past the point where a flat radio list stays scannable, so they're bucketed:
 * **Classic** (System/Light/Dark — the paper-and-ink originals), **Fun** (Ember/Forest/
 * Bubblegum — a different mood, same restraint) and **Wild** (Golden/Cosmos/Tidepool/
 * Hot Rod — the ones that also change the display typeface). Each row still shows a live
 * swatch strip so a member can see a theme's real colors before switching.
 */
export function ThemeSwitcher(): JSX.Element {
  const { preference, setPreference, systemPrefersDark } = useTheme();

  const options: ThemeOption[] = [
    {
      id: 'system',
      label: 'System',
      description: 'Follows your device.',
      group: 'Classic',
      swatchTheme: systemPrefersDark ? 'dark' : 'light',
    },
    {
      id: 'light',
      label: 'Light',
      description: 'Warm paper, ink-black text.',
      group: 'Classic',
      swatchTheme: 'light',
    },
    {
      id: 'dark',
      label: 'Dark',
      description: 'Warm paper on near-black.',
      group: 'Classic',
      swatchTheme: 'dark',
    },
    ...CUSTOM_THEMES.map((t) => ({
      id: t.id,
      label: t.label,
      description: t.description,
      group: t.group as ThemeGroup,
      swatchTheme: t.id,
    })),
  ];

  return (
    <div role="radiogroup" aria-label="Theme">
      {THEME_GROUPS.map((group) => (
        <div key={group}>
          <GroupHeading>{group}</GroupHeading>
          <div className="divide-y divide-line border-t border-line">
            {options
              .filter((option) => option.group === group)
              .map((option) => (
                <PickerRow
                  key={option.id}
                  selected={preference === option.id}
                  label={option.label}
                  description={option.description}
                  testId={`theme-option-${option.id}`}
                  onSelect={() => setPreference(option.id)}
                  preview={<ThemeSwatch theme={option.swatchTheme} />}
                />
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** The four module ids previewed in each icon-set row — the four primary tabs, in nav order. */
const PREVIEW_MODULES = ['calendar', 'lists', 'recipes', 'checkin'] as const;
const PREVIEW_EMOJI: Record<string, string> = {
  calendar: '📅',
  lists: '📝',
  recipes: '📖',
  checkin: '📷',
};

/** Renders the four preview glyphs in a *specific* set (not the active one), so every row
 * shows what picking it would actually look like. */
function IconSetPreview({ set }: { set: IconSetId }): JSX.Element {
  return (
    <span className="flex items-center gap-2 rounded-md border border-line bg-bg px-2 py-1.5 text-fg-2">
      {PREVIEW_MODULES.map((moduleId) => (
        <NavIcon
          key={moduleId}
          moduleId={moduleId}
          emoji={PREVIEW_EMOJI[moduleId]}
          size={20}
          set={set}
        />
      ))}
    </span>
  );
}

/**
 * "Appearance → Icons" picker (owner call, 2026-08-01 — the "super dumb logos" turned out to
 * mean the emoji nav tabs). Each row previews the actual glyphs the nav will render, drawn by
 * the same `NavIcon` resolver the chrome uses, so the preview can't drift from the real thing.
 */
export function IconSetSwitcher(): JSX.Element {
  const { iconSet, setIconSet } = useTheme();

  return (
    <div role="radiogroup" aria-label="Icons" className="divide-y divide-line border-t border-line">
      {ICON_SETS.map((set) => (
        <PickerRow
          key={set.id}
          selected={iconSet === set.id}
          label={set.label}
          description={set.description}
          testId={`icon-set-option-${set.id}`}
          onSelect={() => setIconSet(set.id)}
          preview={<IconSetPreview set={set.id} />}
        />
      ))}
    </div>
  );
}
